<template>

  <Toast :group="page.name" position="bottom-center" />
  <Loader v-if="loader" />

  <div class="page">
    <div class="formgrid grid form-file">
      <div class="field col-12">
        <div class="button-wrapper">
          <span :class="['label', { 'active': fileToUpload.length > 0 }]">
            {{ fileToUpload.length > 0 ? labelFile : 'Carica File' }}
          </span>
          <input id="upload" type="file" ref="file" multiple @change="handleFileUpload($event)" :accept="allowedExtensions.map(e => '.' + e).join(',')" />
        </div>
        <div class="text-center allowed-format"> Formati consentiti: {{ allowedExtensions.join(', ') }} </div>
        <div v-show="validationErrors.files && submitted" class="p-error">Campo richiesto.</div>
        <div v-show="validationErrors.type && submitted" class="p-error">Formato non consentito</div>
      </div>
    </div>

    <div class="report" v-if="report">
      <h4>Esito importazione</h4>
      <p class="mt-2">Fatture importate: <strong>{{ report.imported }}</strong></p>

      <div class="mt-3" v-if="report.duplicati.length">
        <h5>Fatture già presenti (ignorate)</h5>
        <DataTable :value="report.duplicati.map(e => ({ file: e }))" tableStyle="width: 100%">
          <Column field="file" header="File duplicati" />
        </DataTable>
      </div>

      <div class="mt-3" v-if="report.errors.length">
        <h5>File non elaborati</h5>
        <DataTable :value="report.errors.map(e => ({ file: e }))" tableStyle="width: 100%">
          <Column field="file" header="File con errori" />
        </DataTable>
      </div>
    </div>
  </div>

  <div class="wrap-footer-main-dialog">
    <div class="footer-main-dialog">
      <Button
          label="Chiudi"
          icon="pi pi-times"
          iconPos="right"
          class="p-button-secondary mr-3"
          @click="onCancelButton($event)"
        />
        <Button
          label="Carica"
          icon="pi pi-upload"
          iconPos="right"
          class="mr-3"
          @click="onUpload($event)"
        />
    </div>
  </div>

</template>

<script>

import { ref, computed, getCurrentInstance } from 'vue';
import services from '@/config/services'
import Loader from '@/components/Loader.vue'
import { useToast } from "primevue/usetoast";

export default {
  props: {
    data: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  components: {
    Loader
  },

  setup() {
    const page = { name: 'fatture-acquisto-aggiungi' }
    const loader = ref(false)
    const toast = useToast();

    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus
    self.$http = self.appContext.config.globalProperties.$http

    const uploadUrl = `${services.ask_api.url}/management/acquisto/fatture/upload`

    const allowedExtensions = ['xml', 'p7m', 'zip']
    const fileToUpload = ref([])
    const report = ref(null)
    const validationErrors = ref({})
    const submitted = ref(false)

    const labelFile = computed(() => {
      if (fileToUpload.value.length === 1) return fileToUpload.value[0].name
      return `${fileToUpload.value.length} file selezionati`
    })

    const handleFileUpload = (evt) => {
      evt.preventDefault()
      submitted.value = false
      report.value = null
      fileToUpload.value = self.refs.file.files
    }

    const getExtension = (name) => name.split('.').pop().toLowerCase()

    const validateForm = () => {
      if (fileToUpload.value.length === 0) {
        validationErrors.value['files'] = true
        delete validationErrors.value['type']
      } else {
        delete validationErrors.value['files']
      }
      if (fileToUpload.value.length > 0) {
        const valid = [...fileToUpload.value].every(file => allowedExtensions.includes(getExtension(file.name)))
        if (valid) delete validationErrors.value['type']
        else validationErrors.value['type'] = true
      }
      return !Object.keys(validationErrors.value).length
    }

    const showUploadResult = (payload, statusCode) => {
      if (statusCode === 422) {
        toast.add({ severity: 'warn', summary: 'Attenzione', detail: 'Seleziona almeno un file', group: page.name, life: 5000 })
        return
      }

      const imported = payload.imported ?? 0
      const duplicati = payload.duplicati ?? []
      const errors = payload.errors ?? []

      report.value = { imported, duplicati, errors }

      const messaggi = []
      if (imported > 0) {
        messaggi.push({ severity: 'success', summary: 'Importazione', detail: `${imported} fatture importate con successo` })
      }
      if (duplicati.length > 0) {
        messaggi.push({ severity: 'warn', summary: 'Duplicati', detail: `${duplicati.length} fatture già presenti (ignorate): ${duplicati.join(', ')}` })
      }
      if (errors.length > 0) {
        messaggi.push({ severity: 'error', summary: 'Errori', detail: `${errors.length} file non elaborati: ${errors.join(', ')}` })
      }
      if (messaggi.length === 0) {
        messaggi.push({ severity: 'info', summary: 'Importazione', detail: 'Nessuna fattura elaborata' })
      }

      messaggi.forEach((m) => toast.add({ ...m, group: page.name, life: 6000 }))
    }

    const onUpload = async () => {
      submitted.value = true
      toast.removeGroup(page.name)
      if (!validateForm()) return

      report.value = null
      loader.value = true

      const fd = new FormData()
      for (let i = 0; i < fileToUpload.value.length; i++) {
        fd.append('file[]', fileToUpload.value[i])
      }

      try {
        const res = await self.$http.post(uploadUrl, fd, {
          headers: { 'content-type': 'multipart/form-data' }
        })
        showUploadResult(res.data?.data ?? {}, res.data?.meta?.status?.code ?? 200)
      } catch (err) {
        toast.add({ severity: 'error', summary: 'Errore', detail: 'Caricamento fallito', group: page.name, life: 6000 })
      } finally {
        fileToUpload.value = []
        if (self.refs.file) self.refs.file.value = ''
        loader.value = false
      }
    }

    const onCancelButton = () => {
      self.$eventBus.emit(`dialog-return-cancel-button`, {})
    }

    return {
      page,
      loader,
      allowedExtensions,
      fileToUpload,
      labelFile,
      handleFileUpload,
      onUpload,
      report,
      validationErrors,
      submitted,
      onCancelButton,
    }
  }
}


</script>

<style scoped>
.form-file .button-wrapper {
  position: relative;
  width: 100%;
  text-align: center;
}

.form-file .allowed-format {
  font-size: 12px;
  color: rgb(186, 186, 186);
  margin-top: 2px;
}

.form-file .button-wrapper span.label {
  position: relative;
  z-index: 0;
  display: inline-block;
  width: 100%;
  background: #ffffff;
  border: 1px solid rgb(217, 217, 217) !important;
  cursor: pointer;
  color: rgb(168, 168, 168);
  padding: 10px 2px;
  text-transform:uppercase;
  font-size:12px;
  border-radius: 3px;
}

.form-file .button-wrapper span.label.active {
  background: #2196F3;
  color: white;
}

.form-file #upload {
    display: inline-block;
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    opacity: 0;
    cursor: pointer;
}

</style>
